/**
 * Oath Notification Service
 * API calls for notification operations using the new Oath authentication
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// NOTIFICATION ENDPOINTS
// ============================================

/**
 * Get all notifications for the current user
 */
export const getNotifications = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/notifications`);

    if (!response.ok) {
      return { success: false, error: `Failed to fetch notifications: ${response.statusText}` };
    }

    const result = await parseResponse(response);
    return { success: true, data: Array.isArray(result) ? result : [] };
  } catch (error) {
    return { success: false, error: error.message || 'Error fetching notifications' };
  }
};

/**
 * Clear a single notification by ID
 */
export const clearNotification = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/notifications/${id}/clear`, {
      method: 'PATCH',
    });

    if (!response.ok) {
      return { success: false, error: `Failed to clear notification: ${response.statusText}` };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Error clearing notification' };
  }
};

/**
 * Clear all notifications for the current user
 */
export const clearAllNotifications = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/notifications/clear-all`, {
      method: 'PATCH',
    });

    if (!response.ok) {
      return { success: false, error: `Failed to clear notifications: ${response.statusText}` };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Error clearing notifications' };
  }
};
